import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
  SUBSCRIPTION_PLANS,
  createSubscriptionForStudent,
  deleteSubscription,
  revokeSubscription,
  extendSubscriptionById,
  isSubscriptionActive,
  getDaysRemaining,
  normalizeEmail,
  SubscriptionExistsError,
} from '../../utils/subscriptions';
import {
  createStudentAccount,
  getCreateAccountErrorMessage,
} from '../../utils/createStudentAccount';
import { useStudyPlans } from '../../hooks/useStudyPlans';
import PaymentReceipt from '../payments/PaymentReceipt';
import {
  downloadExcelCsv,
  endOfDay,
  startOfMonth,
  startOfWeek,
} from '../../utils/exportExcel';
import { printElementAsPdf } from '../../utils/exportPdf';
import { exportStudentArchive, importStudentArchive } from '../../utils/studentExportImport';
import {
  reactivateStudentPortal,
  approveStudentDevice,
  getDeviceFingerprint,
} from '../../utils/studentSecurity';
import { notifyStudentDeviceApproved } from '../../utils/notifications';
import StudentFullProfileModal from './StudentFullProfileModal';

export default function SubscriptionsPanel() {
  const { currentUser } = useAuth();
  const { plans: studyPlans } = useStudyPlans();
  const planOptions =
    studyPlans.length > 0
      ? studyPlans.map((p) => ({
          id: p.id,
          label: p.name,
          days: p.days,
        }))
      : Object.values(SUBSCRIPTION_PLANS);
  const [subscriptions, setSubscriptions] = useState([]);
  const [form, setForm] = useState({
    studentName: '',
    email: '',
    password: '',
    planId: '1-month',
    createAccount: true,
    customDays: '',
    paymentMethod: 'online',
    paymentDate: new Date().toISOString().slice(0, 10),
    amountPaid: '',
    packageTotal: '',
  });
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [payments, setPayments] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('success');
  const [invoicePayment, setInvoicePayment] = useState(null);
  const [profileSub, setProfileSub] = useState(null);
  const importRef = React.useRef(null);

  async function handleImportArchive(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    setMessage('');
    try {
      const result = await importStudentArchive(file);
      setMessage(`Imported archive for ${result.email}`);
      setMessageTone('success');
    } catch (err) {
      setMessage(err.message || 'Import failed');
      setMessageTone('error');
    } finally {
      setSaving(false);
      e.target.value = '';
    }
  }

  async function handleExportFull(sub) {
    if (!sub.userId) {
      setMessage('Link a Firebase account before exporting full archive.');
      setMessageTone('error');
      return;
    }
    setActionId(sub.id);
    try {
      const purge = window.confirm(
        'Export full student archive?\n\nOK = export JSON and remove student data from server.\nCancel = export only (keep server data).'
      );
      await exportStudentArchive(sub.userId, { purgeFromServer: purge });
      setMessage(purge ? 'Exported and purged from server.' : 'Full archive exported.');
      setMessageTone('success');
    } catch (err) {
      setMessage(err.message || 'Export failed');
      setMessageTone('error');
    } finally {
      setActionId(null);
    }
  }

  async function handleReactivate(userId) {
    if (!userId) return;
    setActionId(userId);
    try {
      await reactivateStudentPortal(userId);
      setMessage('Student portal reactivated.');
      setMessageTone('success');
    } catch (err) {
      setMessage(err.message || 'Reactivate failed');
      setMessageTone('error');
    } finally {
      setActionId(null);
    }
  }

  async function handleApproveDevice(userId) {
    if (!userId) return;
    setActionId(`dev-${userId}`);
    try {
      const snap = await getDoc(doc(db, 'users', userId));
      const pendingId = snap.data()?.pendingDeviceId || getDeviceFingerprint();
      await approveStudentDevice(userId, pendingId);
      await notifyStudentDeviceApproved(userId).catch(console.warn);
      setMessage('Device approved for student.');
      setMessageTone('success');
    } catch (err) {
      setMessage(err.message || 'Approve device failed');
      setMessageTone('error');
    } finally {
      setActionId(null);
    }
  }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'subscriptions'), orderBy('createdAt', 'desc')),
      (snap) => setSubscriptions(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'payments'), orderBy('createdAt', 'desc')),
      (snap) => setPayments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'users'), where('accessStatus', '==', 'pending_payment')),
      (snap) => setPendingUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() }))),
      () => setPendingUsers([])
    );
    return unsub;
  }, []);

  const paymentRefByEmail = useMemo(() => {
    const map = {};
    payments.forEach((p) => {
      const key = (p.email || '').toLowerCase();
      if (key && p.category === 'course' && !map[key]) {
        map[key] = p.referenceNumber;
      }
    });
    return map;
  }, [payments]);

  const paymentRefByUserId = useMemo(() => {
    const map = {};
    payments.forEach((p) => {
      if (p.userId && p.category === 'course' && !map[p.userId]) {
        map[p.userId] = p.referenceNumber;
      }
    });
    return map;
  }, [payments]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.studentName.trim() || !form.email.trim()) return;
    setSaving(true);
    setMessage('');
    setMessageTone('success');
    setInvoicePayment(null);
    try {
      const email = normalizeEmail(form.email);
      const selectedPlan = planOptions.find((p) => p.id === form.planId);
      const customDays = Number(form.customDays) || 0;
      const planDays = customDays > 0 ? customDays : selectedPlan?.days;
      const planLabel =
        customDays > 0 ? `${customDays} Days GED Access (manual)` : selectedPlan?.label;

      const payment = {
        method: form.paymentMethod,
        date: form.paymentDate,
        amount: Number(form.amountPaid) || 0,
        packageTotal: Number(form.packageTotal) || Number(form.amountPaid) || 0,
        currency: 'USD',
        createInvoice: true,
      };

      const clearedForm = {
        studentName: '',
        email: '',
        password: '',
        planId: form.planId,
        createAccount: form.createAccount,
        customDays: '',
        paymentMethod: form.paymentMethod,
        paymentDate: new Date().toISOString().slice(0, 10),
        amountPaid: '',
        packageTotal: '',
      };

      if (form.createAccount) {
        const created = await createStudentAccount({
          studentName: form.studentName,
          email: form.email,
          password: form.password,
          planId: form.planId,
          planDays,
          planLabel,
          createdBy: currentUser?.uid || '',
          payment,
        });
        if (created.payment) setInvoicePayment(created.payment);
        setMessage('Account created. Login details are not shown on this screen.');
        setForm(clearedForm);
      } else {
        const created = await createSubscriptionForStudent({
          studentName: form.studentName,
          email: form.email,
          planId: form.planId,
          planDays,
          planLabel,
          createdBy: currentUser?.uid || '',
          payment,
        });
        if (created.payment) setInvoicePayment(created.payment);
        setForm(clearedForm);
        setMessage('Subscription created.');
      }
    } catch (err) {
      if (err instanceof SubscriptionExistsError) {
        const existing = err.existing;
        setMessageTone('error');
        setMessage(
          `This student already has an active ${existing.planLabel || 'subscription'} `
          + `(${getDaysRemaining(existing)} days left). Extend or revoke the existing plan instead.`
        );
      } else {
        setMessageTone('error');
        setMessage(getCreateAccountErrorMessage(err));
        console.error(err);
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleRevoke(id) {
    if (!window.confirm('Revoke this subscription? The student will lose access immediately.')) return;
    setActionId(id);
    try {
      await revokeSubscription(id);
    } catch (err) {
      alert('Could not revoke subscription.');
      console.error(err);
    } finally {
      setActionId(null);
    }
  }

  async function handleExtend(id) {
    setActionId(id);
    try {
      await extendSubscriptionById(id, 30);
    } catch (err) {
      alert('Could not extend subscription.');
      console.error(err);
    } finally {
      setActionId(null);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Permanently delete this subscription record?')) return;
    setActionId(id);
    try {
      await deleteSubscription(id);
    } catch (err) {
      alert('Could not delete subscription.');
      console.error(err);
    } finally {
      setActionId(null);
    }
  }

  function formatDate(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  const enrolledEmails = useMemo(
    () => new Set(subscriptions.map((s) => (s.email || '').toLowerCase()).filter(Boolean)),
    [subscriptions]
  );

  const unpaidRows = useMemo(() => {
    const fromUsers = pendingUsers
      .filter((u) => !enrolledEmails.has((u.email || '').toLowerCase()))
      .map((u) => ({
        id: `unpaid-${u.uid}`,
        userId: u.uid,
        studentName: u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
        email: u.email,
        registrationNumber: u.registrationNumber || '',
        planLabel: 'Website enrollment — not paid',
        status: 'unpaid',
        unpaidEnrollment: true,
        createdAt: u.createdAt,
        startDate: null,
        endDate: null,
      }));
    const unpaidSubs = subscriptions.filter((sub) => {
      const email = (sub.email || '').toLowerCase();
      return !paymentRefByEmail[email]
        && !paymentRefByUserId[sub.userId]
        && !sub.paid
        && (Number(sub.amountPaid) || 0) <= 0;
    });
    return [...fromUsers, ...unpaidSubs];
  }, [pendingUsers, enrolledEmails, subscriptions, paymentRefByEmail, paymentRefByUserId]);

  const activeCount = subscriptions.filter(isSubscriptionActive).length;

  function subCreatedMs(sub) {
    if (!sub.createdAt) return 0;
    return sub.createdAt.toDate ? sub.createdAt.toDate().getTime() : new Date(sub.createdAt).getTime();
  }

  const filtered = useMemo(() => {
    let list =
      filter === 'unpaid'
        ? unpaidRows
        : filter === 'all'
          ? [
              ...pendingUsers
                .filter((u) => !enrolledEmails.has((u.email || '').toLowerCase()))
                .map((u) => ({
                  id: `unpaid-${u.uid}`,
                  userId: u.uid,
                  studentName: u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
                  email: u.email,
                  registrationNumber: u.registrationNumber || '',
                  planLabel: 'Website enrollment — not paid',
                  status: 'unpaid',
                  unpaidEnrollment: true,
                  createdAt: u.createdAt,
                  startDate: null,
                  endDate: null,
                })),
              ...subscriptions,
            ]
        : filter === 'active'
          ? subscriptions.filter(isSubscriptionActive)
          : subscriptions.filter((sub) => !isSubscriptionActive(sub) && sub.status !== 'unpaid');

    let fromMs = null;
    let toMs = null;
    const now = new Date();
    if (datePreset === 'week') {
      fromMs = startOfWeek(now).getTime();
      toMs = endOfDay(now).getTime();
    } else if (datePreset === 'month') {
      fromMs = startOfMonth(now).getTime();
      toMs = endOfDay(now).getTime();
    } else if (datePreset === 'custom') {
      if (customFrom) fromMs = new Date(`${customFrom}T00:00:00`).getTime();
      if (customTo) toMs = endOfDay(new Date(`${customTo}T00:00:00`)).getTime();
    }

    if (fromMs != null || toMs != null) {
      list = list.filter((sub) => {
        const ms = subCreatedMs(sub);
        if (fromMs != null && ms < fromMs) return false;
        if (toMs != null && ms > toMs) return false;
        return true;
      });
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((sub) => {
        const hay = [
          sub.studentName,
          sub.email,
          sub.registrationNumber,
          sub.planLabel,
          sub.userId,
          paymentRefByUserId[sub.userId] || paymentRefByEmail[(sub.email || '').toLowerCase()],
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [subscriptions, filter, search, datePreset, customFrom, customTo, paymentRefByEmail, paymentRefByUserId, unpaidRows, pendingUsers, enrolledEmails]);

  function handleExportExcel() {
    const headers = ['Payment Ref', 'Plan', 'Start', 'End', 'Status'];
    const rows = filtered.map((sub) => {
      const active = isSubscriptionActive(sub);
      return [
        paymentRefByUserId[sub.userId] || paymentRefByEmail[(sub.email || '').toLowerCase()] || '',
        sub.planLabel || '',
        formatDate(sub.startDate),
        formatDate(sub.endDate),
        active ? 'Active' : sub.status || 'Inactive',
      ];
    });
    downloadExcelCsv(`SBA-subscriptions-${datePreset}.csv`, headers, rows);
  }

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Student accounts & subscriptions</h2>
          <p style={s.sub}>
            Create a student login (email + password) and grant GED study access.
            Each enrolled student gets a unique registration number (SBA-date-digits).
            One active subscription per student.
            {activeCount > 0 && <span style={s.activeBadge}> {activeCount} active</span>}
          </p>
        </div>
      </div>

      {message && <div style={s.banner(messageTone)}>{message}</div>}

      {invoicePayment ? (
        <div style={s.invoiceBox}>
          <div style={s.invoiceHead}>
            <h3 style={s.invoiceTitle}>Enrollment invoice</h3>
            <button type="button" onClick={() => setInvoicePayment(null)} style={s.secondaryBtn}>
              Close
            </button>
          </div>
          <PaymentReceipt payment={invoicePayment} printId="admin-enroll-invoice" />
        </div>
      ) : null}

      <form onSubmit={handleAdd} style={s.addForm}>
        <input
          type="text"
          placeholder="Student full name"
          value={form.studentName}
          onChange={(e) => setForm((p) => ({ ...p, studentName: e.target.value }))}
          style={s.input}
          required
        />
        <input
          type="email"
          placeholder="Student email (login email)"
          value={form.email}
          onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          style={s.input}
          required
        />
        {form.createAccount && (
          <input
            type="text"
            placeholder="Temporary password (min 6 chars)"
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            style={s.input}
            required={form.createAccount}
            minLength={6}
            autoComplete="new-password"
          />
        )}
        <select
          value={form.planId}
          onChange={(e) => setForm((p) => ({ ...p, planId: e.target.value }))}
          style={s.select}
        >
          {planOptions.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.label} ({plan.days} days)
            </option>
          ))}
        </select>
        <input
          type="number"
          min="1"
          placeholder="Custom days (optional, overrides plan)"
          value={form.customDays}
          onChange={(e) => setForm((p) => ({ ...p, customDays: e.target.value }))}
          style={s.input}
        />

        <div style={s.paymentBox}>
          <p style={s.paymentTitle}>Payment details (creates invoice after enroll)</p>
          <div style={s.paymentGrid}>
            <label style={s.payLabel}>
              Payment method
              <select
                value={form.paymentMethod}
                onChange={(e) => setForm((p) => ({ ...p, paymentMethod: e.target.value }))}
                style={s.select}
              >
                <option value="online">Online transfer</option>
                <option value="cash">Cash</option>
              </select>
            </label>
            <label style={s.payLabel}>
              Payment date
              <input
                type="date"
                value={form.paymentDate}
                onChange={(e) => setForm((p) => ({ ...p, paymentDate: e.target.value }))}
                style={s.input}
              />
            </label>
            <label style={s.payLabel}>
              Amount paid (USD)
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 100"
                value={form.amountPaid}
                onChange={(e) => setForm((p) => ({ ...p, amountPaid: e.target.value }))}
                style={s.input}
              />
            </label>
            <label style={s.payLabel}>
              Total package (USD)
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 100"
                value={form.packageTotal}
                onChange={(e) => setForm((p) => ({ ...p, packageTotal: e.target.value }))}
                style={s.input}
              />
            </label>
          </div>
        </div>

        <label style={s.checkLabel}>
          <input
            type="checkbox"
            checked={form.createAccount}
            onChange={(e) => setForm((p) => ({ ...p, createAccount: e.target.checked }))}
          />
          Create login account + send password
        </label>
        <button type="submit" disabled={saving} style={s.addBtn(saving)}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>person_add</span>
          {saving ? 'Creating…' : form.createAccount ? 'Create account & access' : 'Create subscription only'}
        </button>
      </form>

      <div style={s.filters}>
        {[
          { id: 'all', label: 'All' },
          { id: 'unpaid', label: `Unpaid enrollments (${unpaidRows.length})` },
          { id: 'active', label: 'Active' },
          { id: 'inactive', label: 'Expired / Revoked' },
        ].map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setFilter(id)}
            style={s.filterBtn(filter === id)}
          >
            {label}
          </button>
        ))}
        <select
          style={{ ...s.select, flex: '0 1 auto' }}
          value={datePreset}
          onChange={(e) => {
            const v = e.target.value;
            setDatePreset(v);
            if (v === 'custom' && !customFrom) {
              setCustomFrom(new Date().toISOString().slice(0, 10).replace(/-\d{2}$/, '-01'));
              setCustomTo(new Date().toISOString().slice(0, 10));
            }
          }}
        >
          <option value="all">All dates</option>
          <option value="week">This week</option>
          <option value="month">This month</option>
          <option value="custom">Custom range</option>
        </select>
        {datePreset === 'custom' ? (
          <>
            <input type="date" style={s.input} value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <input type="date" style={s.input} value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </>
        ) : null}
        <input
          type="search"
          placeholder="Search name, email, or registration no…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...s.input, flex: '1 1 220px' }}
        />
        <button type="button" onClick={handleExportExcel} style={s.exportBtn} disabled={!filtered.length}>
          Excel
        </button>
        <button
          type="button"
          onClick={() => printElementAsPdf('subscriptions-table-print', 'Subscriptions Report')}
          style={s.exportBtn}
          disabled={!filtered.length}
        >
          PDF
        </button>
        <button type="button" style={s.exportBtn} onClick={() => importRef.current?.click()}>
          Import archive
        </button>
        <input ref={importRef} type="file" accept="application/json,.json" hidden onChange={handleImportArchive} />
      </div>

      {filtered.length === 0 ? (
        <div style={s.empty}>No subscriptions in this view.</div>
      ) : (
        <div style={s.tableWrap} id="subscriptions-table-print">
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Payment Ref</th>
                <th style={s.th}>Plan</th>
                <th style={s.th}>Period</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((sub) => {
                const active = isSubscriptionActive(sub);
                const daysLeft = getDaysRemaining(sub);
                const busy = actionId === sub.id;
                return (
                  <tr key={sub.id} style={s.tr}>
                    <td style={s.td}>
                      <span style={s.regNo}>
                        {paymentRefByUserId[sub.userId] || paymentRefByEmail[(sub.email || '').toLowerCase()] || '—'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={s.plan}>{sub.planLabel || 'GED Study'}</span>
                      {active && <span style={s.daysLeft}>{daysLeft} days left</span>}
                    </td>
                    <td style={s.td}>
                      {formatDate(sub.startDate)} → {formatDate(sub.endDate)}
                    </td>
                    <td style={s.td}>
                      <span style={s.statusBadge(active, sub.status)}>
                        {sub.unpaidEnrollment || sub.status === 'unpaid'
                          ? 'Unpaid'
                          : active
                            ? 'Active'
                            : sub.status === 'revoked'
                              ? 'Revoked'
                              : sub.status === 'active'
                                ? 'Expired'
                                : sub.status}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={s.actions}>
                        <button type="button" onClick={() => setProfileSub(sub)} style={s.extendBtn}>
                          View full profile
                        </button>
                        {active && !sub.unpaidEnrollment && (
                          <>
                            <button type="button" onClick={() => handleExtend(sub.id)} disabled={busy} style={s.extendBtn}>
                              +30 days
                            </button>
                            <button type="button" onClick={() => handleRevoke(sub.id)} disabled={busy} style={s.revokeBtn}>
                              Revoke
                            </button>
                          </>
                        )}
                        {!sub.unpaidEnrollment ? (
                          <button type="button" onClick={() => handleDelete(sub.id)} disabled={busy} style={s.deleteBtn}>
                            Delete
                          </button>
                        ) : null}
                        {sub.userId ? (
                          <>
                            <button type="button" onClick={() => handleExportFull(sub)} disabled={busy} style={s.extendBtn}>
                              Export all
                            </button>
                            <button type="button" onClick={() => handleReactivate(sub.userId)} disabled={busy} style={s.extendBtn}>
                              Reactivate
                            </button>
                            <button type="button" onClick={() => handleApproveDevice(sub.userId)} disabled={busy} style={s.extendBtn}>
                              Approve device
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {profileSub ? (
        <StudentFullProfileModal subscription={profileSub} onClose={() => setProfileSub(null)} />
      ) : null}
    </div>
  );
}

const s = {
  wrap: { flex: 1, minHeight: 0, padding: '24px 28px', overflowY: 'auto' },
  header: { marginBottom: 20 },
  title: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight: 800, color: '#4b00d1', marginBottom: 6 },
  sub: { fontSize: 13, color: '#64748b', lineHeight: 1.6 },
  activeBadge: { color: '#059669', fontWeight: 700 },
  banner: (tone) => ({
    marginBottom: 16,
    padding: '12px 16px',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    ...(tone === 'error'
      ? { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }
      : { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857' }),
  }),
  credsBox: {
    marginBottom: 16,
    padding: 16,
    borderRadius: 14,
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
  },
  credsTitle: { fontWeight: 800, color: '#4b00d1', marginBottom: 10, fontSize: 14 },
  credsLine: { fontSize: 13, color: '#334155', marginBottom: 6, wordBreak: 'break-all' },
  copyBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
    padding: '8px 14px',
    borderRadius: 10,
    border: 'none',
    background: '#4b00d1',
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  invoiceBox: {
    marginBottom: 16,
    padding: 16,
    borderRadius: 14,
    background: '#fff',
    border: '1px solid #e2e8f0',
  },
  invoiceHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  invoiceTitle: { margin: 0, fontSize: 16, fontWeight: 800, color: '#1b365d' },
  secondaryBtn: {
    padding: '8px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    color: '#475569',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  addForm: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
    padding: 16,
    background: '#f8fafc',
    borderRadius: 14,
    border: '1px solid #e2e8f0',
    alignItems: 'center',
  },
  input: {
    flex: '1 1 180px',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
  },
  select: {
    flex: '0 1 220px',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    background: '#fff',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: '#475569',
    fontWeight: 600,
    flex: '1 1 100%',
  },
  paymentBox: {
    flex: '1 1 100%',
    background: '#fff',
    border: '1px dashed #cbd5e1',
    borderRadius: 12,
    padding: '12px 14px',
  },
  paymentTitle: { fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 10 },
  paymentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: 10,
  },
  payLabel: {
    display: 'grid',
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
  },
  addBtn: (disabled) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 18px',
    borderRadius: 10,
    border: 'none',
    background: disabled ? '#94a3b8' : '#4b00d1',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
  }),
  filters: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
  filterBtn: (active) => ({
    padding: '7px 14px',
    borderRadius: 999,
    border: active ? '1.5px solid #4b00d1' : '1.5px solid #e2e8f0',
    background: active ? '#4b00d1' : '#fff',
    color: active ? '#fff' : '#64748b',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  }),
  empty: { textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 14 },
  tableWrap: { overflowX: 'auto', borderRadius: 14, border: '1px solid #e2e8f0' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: 13 },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    background: '#f8fafc',
    color: '#64748b',
    fontWeight: 700,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e2e8f0',
  },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '14px 16px', verticalAlign: 'top', color: '#334155' },
  regNo: { fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: '#1b365d', fontSize: 12 },
  studentName: { display: 'block', fontWeight: 600, color: '#4b00d1' },
  email: { display: 'block', fontWeight: 500 },
  linked: { display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 4 },
  plan: { display: 'block', fontWeight: 600 },
  daysLeft: { display: 'block', fontSize: 11, color: '#059669', marginTop: 4, fontWeight: 700 },
  statusBadge: (active, status) => ({
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    background:
      status === 'unpaid'
        ? '#fff7ed'
        : active
          ? '#d1fae5'
          : status === 'revoked'
            ? '#fee2e2'
            : '#f1f5f9',
    color:
      status === 'unpaid'
        ? '#c2410c'
        : active
          ? '#059669'
          : status === 'revoked'
            ? '#dc2626'
            : '#94a3b8',
  }),
  actions: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  extendBtn: {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1.5px solid #bbf7d0',
    background: '#f0fdf4',
    color: '#059669',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  },
  revokeBtn: {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1.5px solid #fde68a',
    background: '#fffbeb',
    color: '#b45309',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  },
  deleteBtn: {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1.5px solid #fecaca',
    background: '#fff',
    color: '#dc2626',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  },
  exportBtn: {
    padding: '7px 14px',
    borderRadius: 999,
    border: 'none',
    background: '#1b365d',
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
};
